import { useEffect, useState } from "react";
import { SelectPicker } from "rsuite";
import { useAtomValue } from "jotai";

import EditWorkspaceDrawer from "../../components/Drawers/EditWorkspaceDrawer/EditWorkspaceDrawer";
import useUserActivity from "../../api/api-hooks/userActivity/useUserActivity";
import useWorkspaces from "../../api/api-hooks/workspaces/useWorkspaces";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import AppEmptyState from "../../elements/AppEmptyState";
import NotificationsWidget from "./NotificationsWidget";
import ConversationsWidget from "./ConversationsWidget";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import UserActivityHeader from "./UserActivityHeader";
import AppSpinner from "../../elements/AppSpinner";
import UserActivityItem from "./UserActivityItem";

const NoWorkspaces = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <AppEmptyState
        title="Create Your First Workspace"
        description="Your GrowthCloud doesn't have any workspaces. Get started by creating
        your first one and adding your team members to it."
        buttonTitle="Get Started"
        onClick={() => setDrawerOpen(true)}
      />
      <EditWorkspaceDrawer
        open={drawerOpen}
        handleClose={() => setDrawerOpen(false)}
      />
    </>
  );
};

const UserActivity = () => {
  const tenantID = useAtomValue(tenantIDAtom);
  const workspaceID = useAtomValue(workspaceIdAtom);
  const [selected, setSelected] = useState<string | null>(null);
  const { userActivity, userActivityLoading } = useUserActivity();
  const { workspaces, workspacesLoading } = useWorkspaces();

  const mostRecentWorkspace =
    userActivity?.RecentWorkspaces?.[
      userActivity?.RecentWorkspaces?.length - 1
    ];

  // Determine what Workspace Activity to show initially.
  // Either current workspace, or workspace with most recent activity.
  useEffect(() => {
    if (workspacesLoading || userActivityLoading) return;

    if (workspaceID) {
      setSelected(workspaceID);
      return;
    }

    if (!mostRecentWorkspace) {
      setSelected(workspaces?.[0]?.id ?? "None");
      return;
    }

    setSelected(mostRecentWorkspace);
  }, [
    workspaceID,
    mostRecentWorkspace,
    workspacesLoading,
    userActivityLoading,
  ]);

  if (workspacesLoading || userActivityLoading || !tenantID || !selected)
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <AppSpinner size="xl" style={{ fontSize: 20, width: 40, height: 40 }} />
      </div>
    );

  const isWorkspaces = workspaces?.length > 0;
  const workspaceActivities = userActivity?.WorkspacesActivities?.[selected];

  return (
    <>
      <AppHelmet title="Home - GrowthCloud.com" />
      <div className="px-2 px-sm-4 py-3">
        <UserActivityHeader noWorkspaces={!isWorkspaces} />
        <div className="d-flex flex-wrap">
          {!isWorkspaces && <NoWorkspaces />}
          {isWorkspaces && (
            <div
              className="mx-2 my-2  rounded p-2 btn-oauth"
              style={{ flex: "1 1 350px", height: "fit-content" }}
            >
              <div className="d-flex align-items-center justify-content-between flex-wrap px-2 px-sm-5 py-2">
                <h2 className=" mb-0 text-dark">Recent Activity</h2>
                {userActivity && (
                  <div className="d-flex">
                    <i
                      className="mdi mdi-view-dashboard-outline mr-1 "
                      style={{
                        fontSize: 24,
                      }}
                    />
                    <SelectPicker
                      data={workspaces}
                      labelKey="Name"
                      valueKey="id"
                      value={selected}
                      searchable={false}
                      cleanable={false}
                      onChange={(id) => setSelected(id)}
                      style={{ minWidth: 220 }}
                    />
                  </div>
                )}
              </div>
              <hr className="my-2 mx-4" />
              {!userActivity && (
                <div className="p-4 d-flex align-items-center ">
                  <div style={{ fontSize: 60, lineHeight: 1 }} className="mr-3">
                    &#128552;
                  </div>
                  <span>
                    <span className="font-weight-600">
                      No activity has been recorded yet.
                    </span>
                    <br />
                    <span className="font-weight-500">
                      We&apos;ll keep track of the pages and views you work in
                      from now on!
                    </span>
                  </span>
                </div>
              )}
              {userActivityLoading ? (
                <AppSpinner />
              ) : (
                userActivity && (
                  <UserActivityItem
                    workspaceID={selected}
                    workspaceActivities={workspaceActivities}
                  />
                )
              )}
            </div>
          )}

          <div className="mx-2 w-100" style={{ flex: "1 1 250px" }}>
            <NotificationsWidget />
            <ConversationsWidget />
          </div>
        </div>
      </div>
    </>
  );
};

export default UserActivity;
